import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);

function getTokenExp() {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem("user");
    return saved ? JSON.parse(saved) : null;
  });
  const [tokenExpiringSoon, setTokenExpiringSoon] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setTokenExpiringSoon(false);
  }, []);

  const checkTokenExpiry = useCallback(() => {
    const exp = getTokenExp();
    // No readable token but we still think we're logged in → force a clean logout
    // instead of sitting in a broken "logged in but every request 401s" state.
    if (!exp) {
      if (localStorage.getItem("user")) logout();
      setTokenExpiringSoon(false);
      return;
    }
    const timeLeft = exp - Date.now();
    if (timeLeft <= 0) {
      logout(); // expired → drop straight to the login screen
      return;
    }
    const thirtyMinutes = 30 * 60 * 1000;
    setTokenExpiringSoon(timeLeft <= thirtyMinutes);
  }, [logout]);

  useEffect(() => {
    if (!user) {
      setTokenExpiringSoon(false);
      return;
    }
    checkTokenExpiry();
    const interval = setInterval(checkTokenExpiry, 60 * 1000);
    // Also re-check when the user comes back to the tab, so an expiry that
    // happened while the tab was in the background is caught immediately.
    const onFocus = () => checkTokenExpiry();
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [user, checkTokenExpiry]);

  const login = async (username, password) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
    const response = await axios.post(`${baseUrl}/api/loginFun`, {
      username,
      password,
    });
    const { token, user: userData } = response.data;
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    setTokenExpiringSoon(false);
    return userData;
  };

  const isAdmin = user?.role === "admin";

  return (
    <AuthContext.Provider value={{ user, login, logout, isAdmin, tokenExpiringSoon }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
